import {
  useCallback,
  useLayoutEffect,
  useMemo,
  type CSSProperties,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Header,
  Icon,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
} from "@repo/ui";
import { useTheme } from "@repo/tokens";
import { ROUTESWEB, useIsMobile } from "@repo/utils";
import { SELF_LEARN_ROUTES } from "../../../../routes";
import type { MemoryCardReportLocationState } from "./types/memoryReportRoute";
import styles from "./MemoryCardReportPage.module.scss";

/** ≤767: mweb floating card. */
const REPORT_MWEB_MAX_WIDTH_PX = 767;
/** ≥1025: web breadcrumb header. */
const REPORT_WEB_MIN_WIDTH_PX = 1024;

const SELF_LEARN_BASE = `/${ROUTESWEB.APP.SELF_LEARN}`;
const CHAPTER_LIST_HREF = `${SELF_LEARN_BASE}/${SELF_LEARN_ROUTES.CHAPTERLIST}`;
const CHAPTER_DETAILS_HREF = `${CHAPTER_LIST_HREF}/${SELF_LEARN_ROUTES.CHAPTERDETAILS}`;

type AvatarItem = {
  id: number;
  label: string;
  onPress?: () => void;
};

export function MemoryCardReportPage() {
  const { t } = useTranslation("selflearn");
  const { t: tc } = useTranslation("common");
  const { setSubjectKey } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMwebLayout = useIsMobile(REPORT_MWEB_MAX_WIDTH_PX);
  const isWebLayout = !useIsMobile(REPORT_WEB_MIN_WIDTH_PX);
  const isTabletLayout = useIsMobile(REPORT_WEB_MIN_WIDTH_PX) && !isMwebLayout;
  const isWideReportLayout = isWebLayout || isTabletLayout;

  const tabletHeaderTitleStyles = useMemo(
    (): CSSProperties => ({
      fontFamily:
        "var(--typography-headline-small-emphasized-fontFamily), sans-serif",
      fontSize: "var(--typography-headline-small-emphasized-fontSize)",
      fontWeight: 600,
      lineHeight: "var(--typography-headline-small-emphasized-lineHeight)",
      letterSpacing:
        "var(--typography-headline-small-emphasized-letterSpacing)",
    }),
    [],
  );

  const state = location.state as MemoryCardReportLocationState | null;
  const subjectId = state?.subjectId ?? 0;
  const subjectName = state?.subjectName ?? "";
  const chapterId = state?.chapterId ?? 0;
  const chapterName = state?.chapterName ?? "";
  const fromCardOfTheDay = state?.fromCardOfTheDay === true;

  const avatarItems = useMemo(
    (): AvatarItem[] => [
      { id: 1, label: tc("profileMenu.myProfile") },
      { id: 2, label: tc("profileMenu.switchProfile") },
      { id: 3, label: tc("profileMenu.switchProduct") },
    ],
    [tc],
  );

  useLayoutEffect(() => {
    if (!subjectName) return;

    setSubjectKey(subjectName);

    return () => setSubjectKey(null);
  }, [setSubjectKey, subjectName]);

  const handleBack = () => {
    navigate(-1);
  };

  const headerTitle = t("memoryCardReport.title");
  const subjectCrumb = subjectName;
  const chapterCrumb = chapterName;
  const showFullChapterBreadcrumb =
    isWebLayout && subjectCrumb.length > 0 && chapterCrumb.length > 0;
  const showLearnReportBreadcrumb = isWebLayout && fromCardOfTheDay;
  const showHeaderBreadcrumb =
    showFullChapterBreadcrumb || showLearnReportBreadcrumb;

  const handleChapterPress = useCallback(() => {
    navigate(CHAPTER_DETAILS_HREF, {
      replace: true,
      state: {
        chapterId: chapterId,
        chapterName: chapterName,
        subjectId,
        subjectName,
        initialTab: "revise",
      },
    });
  }, [navigate, chapterId, chapterName, subjectId, subjectName]);

  const handleSubjectPress = useCallback(() => {
    navigate(CHAPTER_LIST_HREF, {
      replace: true,
      state: {
        subjectId,
        subjectName,
      },
    });
  }, [navigate, subjectId, subjectName]);

  const handleLearnPress = useCallback(() => {
    navigate(SELF_LEARN_BASE, { replace: true });
  }, [navigate]);

  const breadcrumbFull = useMemo(
    () => (
      <Breadcrumb>
        <BreadcrumbItem key="learn">
          <BreadcrumbLink
            href={SELF_LEARN_BASE}
            isActive={false}
            onClick={(e) => {
              e.preventDefault();
              handleLearnPress();
            }}
          >
            {t("learnText")}
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator key="sep-1" />
        <BreadcrumbItem key="subject">
          <BreadcrumbLink
            href={CHAPTER_LIST_HREF}
            isActive={false}
            onClick={(e) => {
              e.preventDefault();
              handleSubjectPress();
            }}
          >
            {subjectCrumb}
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator key="sep-2" />
        <BreadcrumbItem key="chapter-details">
          <BreadcrumbLink
            href={CHAPTER_DETAILS_HREF}
            isActive={false}
            onClick={(event) => {
              event.preventDefault();
              handleChapterPress();
            }}
          >
            {chapterCrumb}
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator key="sep-3" />
        <BreadcrumbItem key="memory-card-report" isCurrentPage>
          <BreadcrumbLink isActive>{headerTitle}</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>
    ),
    [
      handleLearnPress,
      handleSubjectPress,
      handleChapterPress,
      subjectCrumb,
      chapterCrumb,
      headerTitle,
      t,
    ],
  );

  const breadcrumbLearnReport = useMemo(
    () => (
      <Breadcrumb>
        <BreadcrumbItem key="learn">
          <BreadcrumbLink
            href={SELF_LEARN_BASE}
            isActive={false}
            onClick={(e) => {
              e.preventDefault();
              handleLearnPress();
            }}
          >
            {t("learnText")}
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator key="sep-lr" />
        <BreadcrumbItem key="report" isCurrentPage>
          <BreadcrumbLink isActive>{headerTitle}</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>
    ),
    [handleLearnPress, headerTitle, t],
  );

  const headerBreadcrumb = showFullChapterBreadcrumb
    ? breadcrumbFull
    : showLearnReportBreadcrumb
      ? breadcrumbLearnReport
      : undefined;

  return (
    <div
      className={`${styles.page} ${isTabletLayout ? styles.pageTablet : ""}`}
    >
      <div className={styles.header}>
        <Header
          title={isWebLayout && showHeaderBreadcrumb ? undefined : headerTitle}
          leftVariant="back"
          backIcon="circleBackBtn"
          backIconShadow={true}
          backIconWidth={48}
          backIconHeight={48}
          rightIcon={isWebLayout ? "userProfileIcon" : undefined}
          rightIconItems={avatarItems}
          onBackPress={handleBack}
          breadcrumb={headerBreadcrumb}
          titleAlign="center"
          titleColor="var(--colors-neutral-10)"
          titleSize={16}
          titleStyles={isTabletLayout ? tabletHeaderTitleStyles : undefined}
        />
      </div>

      {isWideReportLayout ? (
        <div className={styles.innerDesktop}>
          <div className={styles.desktopScroll}>
            <div
              className={`${styles.desktopContent} ${isTabletLayout ? styles.desktopContentTablet : ""}`}
            >
              {chapterName ? (
                <p className={styles.chapterDesktop}>{chapterName}</p>
              ) : null}
              <div
                className={`${styles.iconWrapDesktop} ${isTabletLayout ? styles.iconWrapTablet : ""}`}
              >
                <Icon name="winCup" width="100%" height="100%" />
              </div>
              <div className={styles.desktopWelcome}>
                <p className={styles.wellDoneDesktop}>
                  {t("memoryCardReport.wellDone")}
                </p>
                <p
                  className={
                    isTabletLayout
                      ? styles.subtitleTablet
                      : styles.subtitleDesktop
                  }
                >
                  {t("memoryCardReport.subtitle")}
                </p>
              </div>
            </div>
          </div>
          <div className={styles.desktopCtaBar}>
            <button
              type="button"
              className={styles.ctaDesktop}
              onClick={handleBack}
            >
              <span className={styles.ctaLabelDesktop}>{t("continue")}</span>
            </button>
          </div>
        </div>
      ) : (
        <div className={styles.innerNarrow}>
          <div className={styles.narrowScroll}>
            <div className={styles.containerNarrow}>
              <div className={styles.cardNarrow}>
                {chapterName ? (
                  <p className={styles.chapterNarrow}>{chapterName}</p>
                ) : null}
                <div className={styles.iconWrapNarrow}>
                  <Icon name="winCup" width="100%" height="100%" />
                </div>
                <div className={styles.textBlockNarrow}>
                  <p className={styles.wellDoneNarrow}>
                    {t("memoryCardReport.wellDone")}
                  </p>
                  <p className={styles.subtitleNarrow}>
                    {t("memoryCardReport.subtitle")}
                  </p>
                </div>
              </div>
              <div className={styles.narrowCtaBar}>
                <button
                  type="button"
                  className={styles.ctaNarrow}
                  onClick={handleBack}
                >
                  <span className={styles.ctaLabelNarrow}>{t("continue")}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
